<template>
  <div class="cdv2" :class="{ 'cdv2--dialog': embedded }" v-if="course">
    <div class="cdv2__hero">
      <div class="cdv2__hero-copy">
        <span v-if="typeLabel" class="cdv2__badge">
          <v-icon x-small color="#2563eb">mdi-laptop</v-icon>
          {{ typeLabel }}
        </span>
        <h1>{{ title }}</h1>
        <p v-if="parsed.summary" class="cdv2__lead">{{ parsed.summary }}</p>
        <div class="cdv2__pills">
          <!-- Duration stays hidden: the legacy course viewer had this block commented out. -->
          <!-- <span v-if="duration"><v-icon x-small>mdi-clock-outline</v-icon>{{ duration }}</span> -->
          <span v-if="level"><v-icon x-small>mdi-signal-cellular-2</v-icon>{{ level }}</span>
          <span v-if="mode"><v-icon x-small>mdi-monitor</v-icon>{{ mode }}</span>
          <span><v-icon x-small>mdi-calendar-blank-outline</v-icon>{{ dates }}</span>
        </div>
      </div>
      <div class="cdv2__hero-media">
        <img :src="imageSrc" :alt="title" @error="onImageError" />
      </div>
    </div>

    <v-divider class="cdv2__divider mb-2" />

    <div class="cdv2__grid">
      <div class="cdv2__main">
        <section
          v-if="parsed.descriptionHtml || audience || parsed.requisitos.length || parsed.academicLevel"
          id="cdv2-general"
          class="cdv2__card"
        >
          <div
            v-if="parsed.descriptionHtml"
            class="cdv2__prose"
            v-html="parsed.descriptionHtml"
          ></div>

          <div v-if="audience" class="cdv2__block">
            <h2>
              <v-icon color="#2563eb">mdi-account-group-outline</v-icon>
              Who is it for?
            </h2>
            <p>{{ audience }}</p>
          </div>

          <div v-if="parsed.requisitos.length || parsed.academicLevel" id="cdv2-requisitos" class="cdv2__block">
            <h2>
              <v-icon color="#2563eb">mdi-clipboard-text-outline</v-icon>
              Requirements
            </h2>
            <p v-if="parsed.academicLevel"><strong>Required academic level:</strong> {{ parsed.academicLevel }}</p>
            <p v-if="parsed.requisitosIntro">{{ parsed.requisitosIntro }}:</p>
            <ul v-if="parsed.requisitos.length">
              <li v-for="item in parsed.requisitos" :key="item">{{ item }}</li>
            </ul>
          </div>

          <!-- Learning outcomes stay hidden: the legacy course viewer had this block commented out. -->
          <!--
          <div v-if="contentItems.length" id="cdv2-contenidos" class="cdv2__block">
            <h2>
              <v-icon color="#2563eb">mdi-book-open-page-variant-outline</v-icon>
              Learning outcomes
            </h2>
            <ul v-if="contentItems.length > 1" class="cdv2__list">
              <li v-for="(item, index) in contentItems" :key="index">{{ item }}</li>
            </ul>
            <p v-else class="cdv2__prose">{{ contentItems[0] }}</p>
          </div>
          -->
        </section>

        <section v-if="topics.length || keywords.length" class="cdv2__card">
          <div class="cdv2__split">
            <div v-if="keywords.length" class="cdv2__block">
              <h2>
                <v-icon color="#2563eb">mdi-pound</v-icon>
                Keywords
              </h2>
              <div class="cdv2__chips cdv2__chips--soft">
                <span v-for="word in keywords" :key="word">{{ word }}</span>
              </div>
            </div>

            <div v-if="topics.length" class="cdv2__block cdv2__block--end">
              <h2>
                Topics
                <v-icon color="#2563eb">mdi-tag-outline</v-icon>
              </h2>
              <div class="cdv2__chips">
                <span v-for="topic in topics" :key="topic">{{ topic }}</span>
              </div>
            </div>
          </div>
        </section>

        <section v-if="resources.length" id="cdv2-recursos" class="cdv2__card">
          <h2>
            <v-icon color="#2563eb">mdi-paperclip</v-icon>
            Resources
          </h2>
          <ul class="cdv2__resources">
            <li v-for="res in resources" :key="res.resId">
              <a :href="resourceHref(res)" target="_blank" rel="noopener">
                <v-icon small color="#2563eb">
                  {{ res.resType === "File" ? "mdi-file-outline" : "mdi-link-variant" }}
                </v-icon>
                <span>
                  <strong>{{ res.resName }}</strong>
                  <em v-if="res.resDesc">{{ res.resDesc }}</em>
                  <small>{{ res.resType === "File" ? "File" : "Link" }}</small>
                </span>
              </a>
            </li>
          </ul>
        </section>

        <section v-if="course.explanation" class="cdv2__card">
          <h2>
            <v-icon color="#2563eb">mdi-lightbulb-on-outline</v-icon>
            Recommendation for you
          </h2>
          <p class="cdv2__recommend">{{ course.explanation }}</p>
        </section>

        <section id="cdv2-mas" class="cdv2__card">
          <h2>
            <v-icon color="#2563eb">mdi-information-outline</v-icon>
            More information
          </h2>
          <div class="cdv2__info-cols">
            <table
              v-for="(column, index) in infoColumns"
              :key="index"
              class="cdv2__table"
            >
              <tr v-for="row in column" :key="row.label">
                <th>{{ row.label }}</th>
                <td>{{ row.value }}</td>
              </tr>
            </table>
          </div>
        </section>
      </div>

      <aside class="cdv2__side">
        <div class="cdv2__card cdv2__side-card">
          <a
            v-if="enrollHref"
            class="cdv2__btn cdv2__btn--primary"
            :href="enrollHref"
            target="_blank"
            rel="noopener"
          >
            Enroll in this course →
          </a>
          <button
            v-if="!visitor"
            type="button"
            class="cdv2__btn cdv2__btn--ghost"
            @click="onFav"
          >
            <v-icon small :color="isFav ? 'pink' : '#2563eb'">
              {{ isFav ? "mdi-heart" : "mdi-heart-outline" }}
            </v-icon>
            {{ isFav ? "Remove from favorites" : "Add to favorites" }}
          </button>
        </div>

        <div class="cdv2__card">
          <h3>Quick information</h3>
          <ul class="cdv2__facts">
            <!-- <li v-if="duration"><v-icon small>mdi-clock-outline</v-icon><span>Duration</span><b>{{ duration }}</b></li> -->
            <li v-if="level"><v-icon small>mdi-signal-cellular-2</v-icon><span>Level</span><b>{{ level }}</b></li>
            <li v-if="mode"><v-icon small>mdi-monitor</v-icon><span>Format</span><b>{{ mode }}</b></li>
            <li><v-icon small>mdi-calendar-blank-outline</v-icon><span>Dates</span><b>{{ dates }}</b></li>
            <li v-if="typeLabel"><v-icon small>mdi-shape-outline</v-icon><span>Type</span><b>{{ typeLabel }}</b></li>
            <li v-if="cost"><v-icon small>mdi-cash</v-icon><span>Cost</span><b>{{ cost }}</b></li>
          </ul>
        </div>

        <div id="cdv2-entidad" class="cdv2__card">
          <h3>Organizing institution</h3>
          <p class="cdv2__org">{{ course.institution || "Not specified" }}</p>
          <p v-if="course.contact_mail" class="cdv2__meta-line">
            <v-icon x-small>mdi-email-outline</v-icon>
            <a :href="'mailto:' + course.contact_mail">{{ course.contact_mail }}</a>
          </p>
          <p v-if="course.weblink" class="cdv2__meta-line">
            <v-icon x-small>mdi-link-variant</v-icon>
            <a :href="course.weblink" target="_blank" rel="noopener">{{ course.weblink }}</a>
          </p>
        </div>

        <div v-if="course.partner" id="cdv2-contacto" class="cdv2__card">
          <h3>{{ course.partner }}</h3>
          <p class="cdv2__hint">Partner organisation delivering this training.</p>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import {
  parseCourseDescription,
  durationLabel,
  levelLabel,
  modeLabel,
  languageLabel,
  splitChips,
  displayTitle,
  costLabel,
  splitLearningOutcomes,
} from "@/components/v2/courseDisplay.js"

export default {
  name: "CourseDetailPage",
  props: {
    course: { type: Object, required: true },
    visitor: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false },
  },
  data: () => ({
    imageFailed: false,
    resources: [],
  }),
  computed: {
    parsed() {
      return parseCourseDescription(this.course && this.course.description)
    },
    title() {
      return displayTitle(this.course)
    },
    duration() {
      return durationLabel(this.course)
    },
    level() {
      return levelLabel(this.course)
    },
    mode() {
      return modeLabel(this.course)
    },
    typeLabel() {
      return this.course.course_type_name || this.course.type || ""
    },
    cost() {
      return costLabel(this.course)
    },
    dates() {
      if (this.course.init_date && this.course.end_date) {
        return (
          new Date(this.course.init_date).toLocaleDateString() +
          " – " +
          new Date(this.course.end_date).toLocaleDateString()
        )
      }
      return "Dates not specified"
    },
    topics() {
      return splitChips(this.course.courses_topic)
    },
    keywords() {
      return splitChips(this.course.keywords)
    },
    audience() {
      return this.course.learning_outcomes ? "" : ""
    },
    contentItems() {
      return splitLearningOutcomes(this.course && this.course.learning_outcomes)
    },
    language() {
      return languageLabel(this.course)
    },
    isFav() {
      return this.course.fav_id != null
    },
    enrollHref() {
      return this.course.weblink || this.course.archive_name || ""
    },
    imageSrc() {
      const file =
        this.imageFailed || !this.course.image ? "default.jpg" : this.course.image
      return process.env.VUE_APP_API_ENDPOINT + "images/courses/" + file
    },
    infoRows() {
      const dash = "Not specified"
      return [
        { label: "Course type", value: this.typeLabel || dash },
        { label: "Learning resource", value: this.course.media || dash },
        { label: "Access", value: this.course.access || dash },
        { label: "Cost", value: this.cost || dash },
        { label: "License type", value: this.course.license_name || dash },
        { label: "Language", value: this.language || dash },
        { label: "EQF/NQF level", value: (this.course.eqf_nqf_level && this.course.eqf_nqf_level !== "null") ? this.course.eqf_nqf_level : dash },
        { label: "ECVET points", value: this.course.ecvet_points && this.course.ecvet_points !== "null" ? this.course.ecvet_points : dash },
        { label: "Partner", value: this.course.partner || dash },
        { label: "Institution", value: this.course.institution || dash },
      ]
    },
    infoColumns() {
      const rows = this.infoRows
      const mid = Math.ceil(rows.length / 2)
      return [rows.slice(0, mid), rows.slice(mid)]
    },
  },
  watch: {
    "course.course_id": {
      immediate: true,
      handler() {
        this.loadResources()
      },
    },
  },
  methods: {
    onImageError() {
      this.imageFailed = true
    },
    onFav() {
      if (this.isFav) this.$emit("del-fav")
      else this.$emit("add-fav")
    },
    resourceHref(res) {
      if (res.resType === "File") {
        return process.env.VUE_APP_API_ENDPOINT + "images/resources/" + res.resUrl
      }
      return res.resUrl
    },
    async loadResources() {
      this.resources = []
      const entityId = this.course && this.course.course_id
      if (!entityId) return
      try {
        const formData = new FormData()
        formData.append("entityTable", "courses")
        formData.append("entityId", entityId)
        let response = await fetch(
          process.env.VUE_APP_API_ENDPOINT + "common/get-resource",
          {
            method: "POST",
            body: formData,
            headers: {
              "Auth-Token": this.$cookie.get("jwt_token"),
            },
          }
        )
        response = await response.json()
        if (!(response.success || response.ok) || !response.result) return
        this.resources = response.result.map((item) => ({
          resId: item.id,
          resType: item.type,
          resName: item.name,
          resDesc: item.desc,
          resUrl: item.content,
        }))
      } catch (error) {
        this.resources = []
      }
    },
  },
}
</script>

<style scoped>
.cdv2 {
  max-width: 1180px;
  margin: 0 auto;
  padding: 16px 8px 48px;
  color: #0f172a;
}

.cdv2__hero {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 28px;
  align-items: center;
  margin-bottom: 20px;
}

.cdv2__badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 13px;
  font-weight: 600;
  border-radius: 999px;
  padding: 4px 10px;
}

.cdv2__hero-copy h1 {
  font-size: 34px;
  line-height: 1.15;
  margin: 12px 0 12px;
  font-weight: 800;
}

.cdv2__lead {
  color: #475569;
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 16px;
}

.cdv2__pills {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  color: #475569;
  font-size: 14px;
}

.cdv2__pills span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.cdv2__hero-media img {
  width: 100%;
  max-height: 220px;
  object-fit: contain;
  background: #fff;
  border-radius: 16px;
}

.cdv2--dialog {
  padding-top: 4px;
}

.cdv2--dialog .cdv2__hero {
  grid-template-columns: minmax(0, 1.4fr) minmax(240px, 0.6fr);
  gap: 32px;
  align-items: stretch;
  margin-bottom: 16px;
}

.cdv2--dialog .cdv2__hero-copy h1 {
  font-size: 32px;
  margin: 10px 0 16px;
}

.cdv2--dialog .cdv2__hero-media {
  background: #fff;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 28px;
}

.cdv2--dialog .cdv2__hero-media img {
  width: auto;
  max-width: 100%;
  max-height: 128px;
  background: transparent;
  border-radius: 0;
}

.cdv2__divider {
  margin: 8px 0 0;
}

.cdv2__grid {
  display: grid;
  grid-template-columns: 1.7fr 0.9fr;
  gap: 20px;
  align-items: start;
}

.cdv2__card {
  background: #fff;
  border-radius: 16px;
  padding: 22px 24px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  margin-bottom: 16px;
}

.cdv2__card h2,
.cdv2__card h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  margin: 0 0 12px;
}

.cdv2__block {
  margin-top: 22px;
}

.cdv2__split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 22px;
}

.cdv2__split .cdv2__block {
  margin-top: 0;
}

.cdv2__block--end {
  text-align: right;
}

.cdv2__block--end h2,
.cdv2__block--end .cdv2__chips {
  justify-content: flex-end;
}

.cdv2__card > .cdv2__split:first-child {
  margin-top: 0;
}

.cdv2__card > .cdv2__block:first-child,
.cdv2__card > .cdv2__prose:first-child {
  margin-top: 0;
}

.cdv2__list {
  margin: 0;
  padding-left: 1.2em;
  color: #475569;
  line-height: 1.55;
}

.cdv2__list li + li {
  margin-top: 0.35em;
}

.cdv2__resources {
  list-style: none;
  padding: 0;
  margin: 0;
}

.cdv2__resources li + li {
  margin-top: 8px;
}

.cdv2__resources a {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #0f172a;
  text-decoration: none;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.cdv2__resources a:hover {
  border-color: #bfdbfe;
  background: #f8fafc;
}

.cdv2__resources span {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.cdv2__resources em {
  font-style: normal;
  color: #64748b;
  font-size: 13px;
}

.cdv2__resources small {
  color: #2563eb;
  font-size: 12px;
  font-weight: 600;
}

.cdv2__recommend {
  margin: 0;
  color: #475569;
  font-size: 16px;
  line-height: 1.55;
  font-style: italic;
}

.cdv2__prose {
  color: #475569;
  line-height: 1.55;
  white-space: pre-line;
}

.cdv2__prose >>> p {
  margin: 0 0 0.6em;
}

.cdv2__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cdv2__chips span {
  background: #dbeafe;
  color: #1d4ed8;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 13px;
}

.cdv2__chips--soft span {
  background: #f1f5f9;
  color: #334155;
}

.cdv2__info-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 28px;
}

.cdv2__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.cdv2__table th,
.cdv2__table td {
  border-bottom: 1px solid #e2e8f0;
  padding: 10px 8px;
  text-align: left;
  vertical-align: top;
}

.cdv2__table th {
  width: 38%;
  color: #64748b;
  font-weight: 600;
}

.cdv2__side-card .cdv2__btn:first-child {
  margin-top: 0;
}

.cdv2__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  border-radius: 10px;
  padding: 12px 14px;
  font-weight: 600;
  text-decoration: none;
  margin-top: 10px;
  cursor: pointer;
}

.cdv2__btn--primary {
  background: #2563eb;
  color: #fff;
  border: 0;
}

.cdv2__btn--ghost {
  background: #fff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
}

.cdv2__facts {
  list-style: none;
  padding: 0;
  margin: 0;
}

.cdv2__facts li {
  display: grid;
  grid-template-columns: 24px 1fr auto;
  gap: 8px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
}

.cdv2__facts span {
  color: #64748b;
}

.cdv2__org {
  font-weight: 700;
  margin: 0 0 10px;
}

.cdv2__hint {
  font-size: 13px;
  color: #475569;
  margin: 6px 0;
}

.cdv2__meta-line {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #475569;
  margin: 6px 0;
  word-break: break-all;
}

.cdv2__meta-line .v-icon {
  margin-top: 2px;
  flex-shrink: 0;
}

.cdv2__meta-line a {
  color: #2563eb;
}

@media (max-width: 960px) {
  .cdv2__hero,
  .cdv2__grid,
  .cdv2__split,
  .cdv2__info-cols {
    grid-template-columns: 1fr;
  }
}
</style>
